import * as LogRocket from 'logrocket';
import userflow from 'userflow.js';
import { ClientScriptsConfig } from '../types';
import { isDevelopmentEnvironment } from '../helpers';

// Hardcoded script configuration values for production/demo
const GA4_MEASUREMENT_ID = 'G-JE90VWW2GP';
const GA4_ADS_PROPERTY_ID = 'AW-10829754623';
const GTM_CONTAINER_ID = 'GTM-TF3T2PV';
const GTM_STAPE_DOMAIN = 'https://t.runway.team';
const GTM_LOADER_DOMAIN = 'https://load.t.runway.team';
const FB_PIXEL_ID = '1684048711932809';
const REDDIT_PIXEL_ID = 't2_d4jtunnn';
const HUBSPOT_ACCOUNT_ID = '19623452';
const LINKEDIN_PARTNER_ID = '3658394';
const CLAYDAR_SITE_ID = 'cAvB8Ci3Nr';
const MOUSEFLOW_PROJECT_ID = 'fbf94387-6480-449c-a7f0-0a5daf874fd1';

let initialized = false;

export function initializeClientScripts(config: ClientScriptsConfig): void {
    if (initialized) {
        console.log('[ScriptManager] Already initialized, skipping');
        return;
    }
    initialized = true;

    const enabled: string[] = [];
    const disabled: string[] = [];

    if (!config.logrocketDisabled) {
        initializeLogRocket();
        enabled.push('LogRocket');
    } else {
        disabled.push('LogRocket');
    }
    if (!config.userflowDisabled) {
        initializeUserflow();
        enabled.push('Userflow');
    } else {
        disabled.push('Userflow');
    }

    if (!config.ga4Disabled) {
        injectGA4();
        enabled.push('GA4');
    } else {
        disabled.push('GA4');
    }
    if (!config.gtmDisabled) {
        injectGTM();
        enabled.push('GTM');
    } else {
        disabled.push('GTM');
    }
    if (!config.fbPixelDisabled) {
        injectFBPixel();
        enabled.push('FBPixel');
    } else {
        disabled.push('FBPixel');
    }
    if (!config.redditDisabled) {
        injectReddit();
        enabled.push('Reddit');
    } else {
        disabled.push('Reddit');
    }
    if (!config.hubspotDisabled) {
        injectHubSpot();
        enabled.push('HubSpot');
    } else {
        disabled.push('HubSpot');
    }
    if (!config.linkedinDisabled) {
        injectLinkedIn();
        enabled.push('LinkedIn');
    } else {
        disabled.push('LinkedIn');
    }
    if (!config.claydarDisabled) {
        injectClaydar();
        enabled.push('Claydar');
    } else {
        disabled.push('Claydar');
    }
    if (!config.mouseflowDisabled) {
        injectMouseflow();
        enabled.push('Mouseflow');
    } else {
        disabled.push('Mouseflow');
    }

    console.log(`[ScriptManager] Enabled: [${enabled.join(', ')}]`);
    console.log(`[ScriptManager] Disabled: [${disabled.join(', ')}]`);
}

function injectGA4(): void {
    // Load gtag.js
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(gtagScript);

    // Initialize dataLayer and gtag
    const initScript = document.createElement('script');
    initScript.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', '${GA4_ADS_PROPERTY_ID}');
    `;
    document.head.appendChild(initScript);
}

function injectGTM(): void {
    // Ensure dataLayer exists
    (window as any).dataLayer = (window as any).dataLayer || [];

    // GTM head script (Stape loader with UID logic)
    const headScript = document.createElement('script');
    headScript.textContent = `
        !(function () {
            'use strict';
            function l(e) {
                for (var t = e, r = 0, n = document.cookie.split(';'); r < n.length; r++) {
                    var o = n[r].split('=');
                    if (o[0].trim() === t) return o[1];
                }
            }
            function s(e) {
                return localStorage.getItem(e);
            }
            function u(e) {
                return window[e];
            }
            function A(e, t) {
                e = document.querySelector(e);
                return (
                    t ?
                        null == e ?
                            void 0
                        :   e.getAttribute(t)
                    : null == e ? void 0
                    : e.textContent
                );
            }
            var e = window,
                t = document,
                r = 'script',
                n = 'dataLayer',
                o = '${GTM_STAPE_DOMAIN}',
                a = '${GTM_LOADER_DOMAIN}',
                i = '8y6wqnvtbueyb',
                c = '71vszk06=aWQ9R1RNLVRGM1QyUFY%3D&page=1',
                g = 'stapeUserId',
                v = '',
                E = '',
                d = !1;
            try {
                var d =
                        !!g &&
                        ((m = navigator.userAgent),
                        !!(m = new RegExp('Version/([0-9._]+)(.*Mobile)?.*Safari.*').exec(m))) &&
                        16.4 <= parseFloat(m[1]),
                    f = 'stapeUserId' === g,
                    I =
                        d && !f ?
                            (function (e, t, r) {
                                void 0 === t && (t = '');
                                var n = { cookie: l, localStorage: s, jsVariable: u, cssSelector: A },
                                    t = Array.isArray(t) ? t : [t];
                                if (e && n[e])
                                    for (var o = n[e], a = 0, i = t; a < i.length; a++) {
                                        var c = i[a],
                                            c = r ? o(c, r) : o(c);
                                        if (c) return c;
                                    }
                                else console.warn('invalid uid source', e);
                            })(g, v, E)
                        :   void 0;
                d = d && (!!I || f);
            } catch (e) {
                console.error(e);
            }
            var m = e,
                g =
                    ((m[n] = m[n] || []),
                    m[n].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' }),
                    t.getElementsByTagName(r)[0]),
                v = I ? '&bi=' + encodeURIComponent(I) : '',
                E = t.createElement(r),
                f = (d && (i = 8 < i.length ? i.replace(/([a-z]{8}$)/, 'kp$1') : 'kp' + i), !d && a ? a : o);
            (E.async = !0), (E.src = f + '/' + i + '.js?' + c + v), null != (e = g.parentNode) && e.insertBefore(E, g);
        })();
    `;
    document.head.appendChild(headScript);

    // GTM noscript (body)
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `${GTM_LOADER_DOMAIN}/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
}

function injectFBPixel(): void {
    const script = document.createElement('script');
    script.textContent = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${FB_PIXEL_ID}');
        fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.setAttribute('height', '1');
    img.setAttribute('width', '1');
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.head.appendChild(noscript);
}

function injectReddit(): void {
    const script = document.createElement('script');
    script.textContent = `
        !function(w,d){if(!w.rdt){var p=w.rdt=function(){
        p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};
        p.callQueue=[];var t=d.createElement('script');
        t.src='https://www.redditstatic.com/ads/pixel.js';t.async=!0;
        var s=d.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(t,s)}}(window,document);
        rdt('init','${REDDIT_PIXEL_ID}', {optOut:false,useDecimalCurrencyValues:true});
        rdt('track','PageVisit');
    `;
    document.head.appendChild(script);
}

function injectHubSpot(): void {
    // CSS to hide HubSpot message container until userflow takes over
    const style = document.createElement('style');
    style.textContent = '#hubspot-messages-iframe-container { visibility: hidden; }';
    document.head.appendChild(style);

    // HubSpot embed script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = `//js.hs-scripts.com/${HUBSPOT_ACCOUNT_ID}.js`;
    document.body.appendChild(script);

    const span = document.createElement('span');
    span.id = `CollectedForms-${HUBSPOT_ACCOUNT_ID}`;
    document.body.appendChild(span);
}

function injectLinkedIn(): void {
    const initScript = document.createElement('script');
    initScript.type = 'text/javascript';
    initScript.textContent = `
        _linkedin_partner_id = '${LINKEDIN_PARTNER_ID}';
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `;
    document.body.appendChild(initScript);

    const loaderScript = document.createElement('script');
    loaderScript.type = 'text/javascript';
    loaderScript.textContent = `
        (function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}var s=document.getElementsByTagName('script')[0];
        var b=document.createElement('script');b.type='text/javascript';b.async=true;
        b.src='https://snap.licdn.com/li.lms-analytics/insight.min.js';
        s.parentNode.insertBefore(b,s)})(window.lintrk);
    `;
    document.body.appendChild(loaderScript);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.setAttribute('height', '1');
    img.setAttribute('width', '1');
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
}

function injectClaydar(): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://static.claydar.com/init.v1.js?id=${CLAYDAR_SITE_ID}`;
    document.head.appendChild(script);
}

function injectMouseflow(): void {
    (window as any)._mfq = (window as any)._mfq || [];
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = `//cdn.mouseflow.com/projects/${MOUSEFLOW_PROJECT_ID}.js`;
    document.head.appendChild(script);
}

function initializeLogRocket(): void {
    if (isDevelopmentEnvironment()) {
        return;
    }
    LogRocket.init('wbldyd/runway', {
        mergeIframes: true,
        rootHostname: 'runway.team',
    });
}

function initializeUserflow(): void {
    userflow.init(USERFLOW_TOKEN);
}
